import { Slice } from "@reduxjs/toolkit";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query";
import createApiSlice, {
  ApiSliceState,
} from "../../../../common/redux/api-slice";
import { AuthActionPayload } from "../../auth-slice/types";
import { FavoriteActionPayload } from "../../favorites-slice/types";
import {
  transformAuthenticatedResponse,
  transformFavoriteErrorResponse,
  transformFavoriteResponse,
  transformNotificationsResponse,
  transformSpecificNotificationResponse,
} from "./transformers";
import {
  ApiResponse,
  AuthenticatedRequest,
  CheckFavoriteRequest,
  GoogleSigninResponse,
  PingSessionRequest,
  RegisterRequest,
  SendPageViewRequest,
  SaveStudyPlanProgressRequest,
  SaveSimuladoProgressRequest,
  StartTrialRequest,
  SpecificNotificationRequest,
  UpdateFavoriteRequest,
  UpdateUserRequest,
  AddTransactionTrackingResponse,
  AddTransactionTrackingRequest,
} from "./types";
import {
  GetPendingNotificationsActionPayload,
  SpecificNotificationActionPayload,
} from "../../notifications-slice/types";
import { checkRegisterModal } from "../../../utils/registerModal";

export { selectors as apiStatusSelectors } from "../../../../common/redux/api-slice";

const api = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: window.location.origin,
  }),
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    googleLogin: builder.mutation<AuthActionPayload, GoogleSigninResponse>({
      query: (data) => ({
        url: "loginGoogle/",
        method: "POST",
        body: JSON.stringify({ pathname: window.location.pathname, ...data }),
      }),
      transformResponse: transformAuthenticatedResponse,
      onQueryStarted: (data, { queryFulfilled }) => {
        queryFulfilled
          .then((result) => {
            checkRegisterModal(result.data);
          })
          .catch((error) => {
            console.error(error);
          });
      },
    }),

    validateToken: builder.mutation<AuthActionPayload, AuthenticatedRequest>({
      query: (data) => ({
        url: "accountUser/",
        method: "POST",
        body: JSON.stringify({
          action: "validateToken",
          pathname: window.location.pathname,
          ...data,
        }),
      }),
      transformResponse: transformAuthenticatedResponse,
      onQueryStarted: (data, { queryFulfilled }) => {
        queryFulfilled
          .then((result) => {
            checkRegisterModal(result.data);
          })
          .catch((error) => {
            console.error(error);
          });
      },
    }),

    register: builder.mutation<AuthActionPayload, RegisterRequest>({
      query: (data) => ({
        url: "accountUser/",
        method: "POST",
        body: JSON.stringify({ action: "register", ...data }),
      }),
      transformResponse: transformAuthenticatedResponse,
    }),

    updateUser: builder.mutation<AuthActionPayload, UpdateUserRequest>({
      query: (data) => ({
        url: "accountUser/",
        method: "POST",
        body: JSON.stringify({ action: "update", ...data }),
      }),
      transformResponse: transformAuthenticatedResponse,
    }),

    deleteAccount: builder.mutation<ApiResponse, AuthenticatedRequest>({
      query: (data) => ({
        url: "accountUser/",
        method: "POST",
        body: JSON.stringify({ action: "deactivate", ...data }),
      }),
    }),

    updateFavorite: builder.mutation<
      FavoriteActionPayload,
      UpdateFavoriteRequest
    >({
      query: (data) => ({
        url: "accountUser/",
        method: "POST",
        body: JSON.stringify({ action: "addFavorite", ...data }),
      }),
      transformResponse: transformFavoriteResponse,
      transformErrorResponse: transformFavoriteErrorResponse,
    }),

    checkIsFavorite: builder.mutation<
      FavoriteActionPayload,
      CheckFavoriteRequest
    >({
      query: (data) => ({
        url: "accountUser/",
        method: "POST",
        body: JSON.stringify({ action: "isFavorite", ...data }),
      }),
      transformResponse: transformFavoriteResponse,
      transformErrorResponse: transformFavoriteErrorResponse,
    }),

    pingSession: builder.mutation<void, PingSessionRequest>({
      query: (data) => ({
        url: "achievement/",
        method: "POST",
        body: JSON.stringify({ action: "stats", ...data }),
      }),
    }),

    readNotification: builder.mutation<
      SpecificNotificationActionPayload,
      SpecificNotificationRequest
    >({
      query: (data) => ({
        url: "achievement/",
        method: "PUT",
        body: JSON.stringify({ action: "readNotifications", ...data }),
      }),
      transformResponse: transformSpecificNotificationResponse,
    }),

    deliverNotification: builder.mutation<
      SpecificNotificationActionPayload,
      SpecificNotificationRequest
    >({
      query: (data) => ({
        url: "achievement/",
        method: "PUT",
        body: JSON.stringify({ action: "deliveredNotifications", ...data }),
      }),
      transformResponse: transformSpecificNotificationResponse,
    }),

    getPendingNotifications: builder.mutation<
      GetPendingNotificationsActionPayload,
      AuthenticatedRequest
    >({
      query: (data) => ({
        url: "achievement/",
        method: "POST",
        body: JSON.stringify({ action: "pendingNotifications", ...data }),
      }),
      transformResponse: transformNotificationsResponse,
    }),

    sendPageView: builder.mutation<void, SendPageViewRequest>({
      query: (data) => ({
        url: "achievement/",
        method: "POST",
        body: JSON.stringify({ action: "pageView", ...data }),
      }),
    }),

    saveStudyPlanProgress: builder.mutation<void, SaveStudyPlanProgressRequest>(
      {
        query: (data) => ({
          url: "api/enem/",
          method: "POST",
          body: JSON.stringify({ action: "save-progress", ...data }),
        }),
      }
    ),

    startTrial: builder.mutation<ApiResponse, StartTrialRequest>({
      query: (data) => ({
        url: "api/enem/",
        method: "POST",
        body: JSON.stringify({ action: "start-trial", ...data }),
      }),
    }),

    addTransactionTracking: builder.mutation<
      AddTransactionTrackingResponse,
      AddTransactionTrackingRequest
    >({
      query: (data) => ({
        url: "accountUser/",
        method: "POST",
        body: JSON.stringify({ action: "addTransactionTracking", ...data }),
      }),
    }),

    saveSimuladoProgress: builder.mutation<void, SaveSimuladoProgressRequest>(
        {
          query: (data) => ({
            url: "api/simulados/",
            method: "POST",
            body: JSON.stringify({ action: "save-progress", ...data }),
          }),
        }
    ),
  }),
});

export const apiStatusSlice = createApiSlice(
  "apiStatus",
  api.endpoints
) as Slice<ApiSliceState, {}, "apiStatus">;

export default api;
